import React, {useState, useEffect, useCallback} from 'react';
import {DataGrid} from '@mui/x-data-grid';
import {Box, Button, Typography} from '@mui/material';
import moment from 'moment-timezone';
import {useLazyQuery} from "@apollo/client";
import {useLocation, useNavigate} from 'react-router-dom';

import {useTimezone} from '../../hooks/useTimezone';
import useOpenUrlInNewTab from "../../hooks/useOpenUrlInNewTab";

import {NAVIGATION_PATHS} from '../../config/NavigationPaths';
import {DATE_FORMAT_IN_COMPACT_READABLE_FORMAT, TWENTY_FOUR_HOUR_TIME_FORMAT} from '../../config/DateTimeConstants';
import SessionHealthDataFetcher from './../sessions/health/SessionHealthDataFetcher';
import {MAX_MEMBER_CHIPS_TO_DISPLAY} from '../../constants/SessionConstants';
import {LOOKUP_SESSION_WITH_CHILDREN_DETAILS} from "../../queries/GraphQLQueries";
import {buildMemberLookupUrl} from "../../views/ParentInfoLookupView";
import {DAY_DIFFERENCE_LABELS} from "../../helpers/ShiftPlanHelper";

import NoRowsOverlayLabel from '../common/NoRowsOverlayLabel';
import RenderZoomLinkCell from './RenderZoomLinkCell';
import RenderServiceTypeWithContentCell from './RenderServiceTypeWithContentCell';
import MemberStatusCell from '../common/MemberStatusCell';

const dataGridSx = {
  "& .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    whiteSpace: 'normal',
    lineHeight: '1.125rem !important',
    backgroundColor: 'var(--mui-common-white)',
    fontSize: '0.875rem',
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: 'var(--mui-common-white)',
    fontSize: '0.875rem',
  },
  "& .MuiDataGrid-root": {
    border: 'none',
    minWidth: '18.75rem',
    minHeight: '12.5rem',
  },
  "& .MuiDataGrid-virtualScroller": {
    overflow: 'scroll !important'
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: 'var(--mui-common-white)',
  },
  "& .MuiTablePagination-displayedRows": {
    fontSize: '0.875rem',
    lineHeight: 1.43,
    letterSpacing: '0.01071em',
  },
  "&&& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: 'var(--mui-common-white)',
  },
  "&&& .MuiDataGrid-row:focus": {
    backgroundColor: 'var(--mui-common-white)',
    outline: 'none',
  },
};

const centeredCellContentStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  textAlign: 'center',
};

const SessionsTable = ({ refetchSessionsData, sessions, noRowsMessage, rowCount, paginationModel, onPaginationModelChange }) => {
  const { timezone } = useTimezone();
  const openUrlInNewTab = useOpenUrlInNewTab();
  const navigate = useNavigate();
  const {state} = useLocation();
  const {data: swappedSessionData} = state || {};
  const [hasRefetchedSwappedSessionData, setHasRefetchedSwappedSessionData] = useState(false);

  const [lookupSession, { data }] = useLazyQuery(LOOKUP_SESSION_WITH_CHILDREN_DETAILS, { fetchPolicy: 'network-only' });
  const [childrenBySession, setChildrenBySession] = useState({});

  useEffect(() => {
    if (swappedSessionData?.data?.swapSession && !hasRefetchedSwappedSessionData) {
      refetchSessionsData();
      setHasRefetchedSwappedSessionData(true);
    }
  }, [swappedSessionData, hasRefetchedSwappedSessionData, refetchSessionsData]);

  useEffect(() => {
    if (sessions && sessions.length > 0) {
      sessions.forEach(session => {
        const sessionId = session.id;
        if (session.bookingsCount > 0 && !childrenBySession[sessionId]) {
          lookupSession({ variables: { sessionId: parseInt(sessionId) } });
        }
      });
    }
  }, [sessions, lookupSession, childrenBySession]);

  useEffect(() => {
    if (data && data?.sessionInfo) {
      const sessionId = data.sessionInfo.id;
      const childrenInSession = data.sessionInfo.seats
        .filter(seat => !!seat.booking)
        .map(seat => seat.booking.child);

      setChildrenBySession(prev => ({
        ...prev,
        [sessionId]: childrenInSession,
      }));
    }
  }, [data]);

  const lookupMember = useCallback((memberId) => {
    const url = buildMemberLookupUrl(memberId);
    openUrlInNewTab(url);
  }, [openUrlInNewTab]);

  const deepCopy = useCallback((obj) => {
    return JSON.parse(JSON.stringify(obj));
  }, []);

  const handleSwapSession = useCallback((session) => {
    const sourceSessionToSwap = deepCopy(session);

    const updatedSourceSession = {
      ...sourceSessionToSwap,
      displayName: sourceSessionToSwap.performer?.displayName || 'Unknown',
      session: sourceSessionToSwap
    };

    setHasRefetchedSwappedSessionData(false);
    navigate(NAVIGATION_PATHS.LIVE_SWAP_SESSION, {state: {sourceSessionToSwap: updatedSourceSession}});
  }, [sessions, deepCopy, navigate]);

  const formatSessionTimeWithOffset = (sessionStartTime, sessionEndTime) => {
    const now = moment.tz(timezone);
    const sessionStart = moment.tz(sessionStartTime, DATE_FORMAT_IN_COMPACT_READABLE_FORMAT, timezone);
    const sessionEnd = moment.tz(sessionEndTime, DATE_FORMAT_IN_COMPACT_READABLE_FORMAT, timezone);
    
    const formattedStartTime = sessionStart.format(TWENTY_FOUR_HOUR_TIME_FORMAT);
    const formattedEndTime = sessionEnd.format(TWENTY_FOUR_HOUR_TIME_FORMAT);

    let dayDifferenceLabel = null;

    if (sessionStart.isAfter(now, 'day')) {
      dayDifferenceLabel = DAY_DIFFERENCE_LABELS.NEXT_DAY;
    } else if (sessionStart.isBefore(now, 'day')) {
      dayDifferenceLabel = DAY_DIFFERENCE_LABELS.PREVIOUS_DAY;
    }

    return (
      <>
        {formattedStartTime} - {formattedEndTime} {dayDifferenceLabel && <Typography sx={{ml:'0.2rem'}}>{dayDifferenceLabel}</Typography>}
      </>
    );
  };

  const renderMembersCell = (params) => {
    const sessionId = params.row.sessionId;
    const childCount = params.row.childCount || 0;
    const shouldShowChildChips = childCount !== 0 && childCount <= MAX_MEMBER_CHIPS_TO_DISPLAY;
    const children = childrenBySession[sessionId] || [];
    const memberIds = params.value.split(',').map(id => id.trim());
    const sessionData = sessions.find(session => session.id === sessionId);

    if (!shouldShowChildChips || memberIds.length === 0 || children.length === 0 || !sessionData) {
      return null;
    }

    return (
      <MemberStatusCell
        children={children}
        session={sessionData}
        timezone={timezone}
        onChildDetailsDrilledInto={lookupMember}
      />
    );
  };

  const sessionsRows = (sessions || [])
    .map((session) => {
      if (!session) return null;

      const fromDate = session.from ? moment.tz(session.from, timezone) : moment.tz(timezone);
      const toDate = session.to ? moment.tz(session.to, timezone) : moment.tz(timezone);
      const sessionStartTime = `${fromDate.format(DATE_FORMAT_IN_COMPACT_READABLE_FORMAT)}`;
      const sessionEndTime = `${toDate.format(DATE_FORMAT_IN_COMPACT_READABLE_FORMAT)}`;

      const service = session.serviceType?.keycode || '';
      const performer = session.performer?.displayName || '';
      const status = '';
      const members = ((session.seats || []).map(seat => seat?.booking?.child?.parent?.externalId || '').filter(Boolean).slice(0, MAX_MEMBER_CHIPS_TO_DISPLAY) || []).join(', ') || '';
      const region = ((session.seats || []).map(seat => seat?.booking?.child?.parent?.region || '').filter(Boolean).slice(0, MAX_MEMBER_CHIPS_TO_DISPLAY) || []).join(', ') || '';
      const zoomLink = '';

      return {
        session,
        id: 0,
        sessionId: session.id,
        sessionStartTime,
        sessionEndTime,
        service,
        performer,
        status,
        members,
        region,
        zoomLink,
        contents: session.contents || [],
        childCount: session.bookingsCount || 0,
      };
    })
    .filter(Boolean)
    .map((row, index) => ({
      ...row,
      id: index + 1,
    }));

  const containerStyle = {width: '100%', minWidth: '18.75rem', minHeight: '12.5rem'};

  const options = {
    enableSessionHealthDetailDisplay: true,
    enableSessionBookedHealthIndicator: true,
    enableZoomLicenseHealthIndicator: true,
    enableZoomLinkHealthIndicator: false,
    enableCohostsAddedHealthIndicator: true,
    enablePerformerCheckInHealthIndicator: true,
    enablePerformerJoinedHealthIndicator: true,
    enableRecordingsAvailableHealthIndicator: true
  };

  const columns = [
    {
      field: 'sessionTime',
      headerName: 'Session Time',
      width: 160,
      wrapText: true,
      renderCell: (params) => {
        const {sessionStartTime, sessionEndTime} = params.row;
        return formatSessionTimeWithOffset(sessionStartTime, sessionEndTime);
      }
    },
    {
      field: 'service',
      headerName: 'Service',
      width: 150,
      wrapText: true,
      renderCell: RenderServiceTypeWithContentCell,
    },
    {
      field: 'performer',
      headerName: 'Performer',
      width: 120,
      wrapText: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      headerAlign: 'center',
      wrapText: true,
      renderCell: (params) => {
        const sessionId = params.row.sessionId;
        const session = sessions.find(session => session.id === sessionId);

        return (
          <Box sx={centeredCellContentStyle}>
            {
              options.enableSessionHealthDetailDisplay
              &&
              <SessionHealthDataFetcher session={session} options={options}/>
            }
          </Box>
        );
      },
    },
    {
      field: 'members',
      headerName: 'Members',
      width: 140,
      wrapText: true,
      renderCell: renderMembersCell,
    },
    {
      field: 'region',
      headerName: 'Region',
      width: 90,
      wrapText: true,
      renderCell: (params) => {
        if (params.row.childCount > MAX_MEMBER_CHIPS_TO_DISPLAY) {
          return null;
        }

        return (
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.3}}>
            {params.value.split(',').map((region, index) => (
              <Typography key={index} variant="body2" sx={{fontSize: '0.875rem'}}>
                {region.trim()}
              </Typography>
            ))}
          </Box>
        )
      },
    },
    {
      field: 'zoomLink',
      headerName: 'Zoom Link',
      width: 150,
      wrapText: true,
      renderCell: RenderZoomLinkCell,
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 100,
      headerAlign: 'center',
      wrapText: true,
      renderCell: (params) => {
        const session = params.row.session;
        const isSessionComplete = moment(session.to).isAfter();
        
        return (
          <Button
            variant="contained"
            size="small"
            sx={{marginLeft: '0.6rem'}}
            onClick={() => handleSwapSession(session)}
            disabled={!isSessionComplete}>
            Swap
          </Button>
        );
      }
    }
  ];

  return (
    <div style={containerStyle}>
      <DataGrid
        pagination
        paginationMode="server"
        rows={sessionsRows}
        columns={columns}
        paginationModel={paginationModel}
        rowCount={rowCount}
        onPaginationModelChange={onPaginationModelChange}
        disableSelectionOnClick
        disableRowSelectionOnClick
        autoHeight
        style={{height: '100%', width: '100%', minWidth: '10.75rem'}}
        sx={dataGridSx}
        getRowHeight={(params) => {
          const membersLines = params.model.members ? params.model.members.split(',').length : 1;
          const regionsLines = params.model.region ? params.model.region.split(',').length : 1;
          const maxLines = Math.max(membersLines, regionsLines);
          const baseHeight = 40;
          const lineHeight = 15;

          if (params.model.childCount > MAX_MEMBER_CHIPS_TO_DISPLAY) {
            return baseHeight;
          }

          return baseHeight + (maxLines - 1) * lineHeight;
        }}
        slots={{
          noRowsOverlay: () => <NoRowsOverlayLabel message={noRowsMessage} />
        }}
        disableColumnMenu
        disableColumnSorting
      />
    </div>
  );
};

export default SessionsTable;